import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";

import { AdminTopbar } from "@/components/admin/AdminTopbar";
import { AdminCard, ListState } from "@/components/admin/primitives";
import { OrdersTab } from "@/components/admin/relatorios/OrdersTab";
import { OverviewTab } from "@/components/admin/relatorios/OverviewTab";
import { PeriodPicker } from "@/components/admin/relatorios/PeriodPicker";
import { RetentionTab } from "@/components/admin/relatorios/RetentionTab";
import { ResellersTab } from "@/components/admin/relatorios/ResellersTab";
import {
  queryToSearch,
  searchToQuery,
  validateCampaignSearch,
  type CampaignSearch,
} from "@/lib/campaigns";
import { errorMessage } from "@/lib/errors";
import {
  downloadReport,
  periodLabel,
  presetPeriod,
  useOrdersReport,
  useOverviewReport,
  useRetentionReport,
  type PeriodFilter,
} from "@/lib/reports";
import { useSession } from "@/lib/session";
import { cn } from "@/lib/utils";

type Tab = "geral" | "pedidos" | "retencao" | "revendedores";

const TABS: { value: Tab; label: string }[] = [
  { value: "geral", label: "Visão geral" },
  { value: "pedidos", label: "Pedidos" },
  { value: "retencao", label: "Retenção" },
  { value: "revendedores", label: "Revendedores" },
];

const EXPORTS = [
  { kind: "vendas", label: "Vendas" },
  { kind: "pecas", label: "Peças" },
  { kind: "clientes", label: "Clientes" },
] as const;

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Aba e período; na aba de revendedores, também os filtros de campanha. */
type Search = CampaignSearch & {
  aba?: Tab;
  de?: string;
  ate?: string;
};

/**
 * Relatórios consolidados — só ADMIN.
 *
 * Aba e período ficam na URL: relatório é coisa que se compartilha ("olha
 * setembro") e que se reabre depois de um F5 sem refazer o filtro. Um filtro
 * só, no topo, vale para tudo abaixo dele — os números das abas sempre falam
 * do mesmo recorte.
 */
export const Route = createFileRoute("/admin/_painel/relatorios")({
  validateSearch: (search: Record<string, unknown>): Search => {
    const out: Search = search["aba"] === "revendedores" ? validateCampaignSearch(search) : {};
    if (TABS.some((tab) => tab.value === search["aba"])) out.aba = search["aba"] as Tab;
    if (typeof search["de"] === "string" && DATE.test(search["de"])) out.de = search["de"];
    if (typeof search["ate"] === "string" && DATE.test(search["ate"])) out.ate = search["ate"];
    return out;
  },
  head: () => ({ meta: [{ title: "Relatórios — Painel MonteBox" }] }),
  component: Relatorios,
});

function Relatorios() {
  const { profile } = useSession();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const aba = search.aba ?? "geral";
  const fallback = presetPeriod("mes");
  const period: PeriodFilter = {
    from: search.de ?? fallback.from,
    to: search.ate ?? fallback.to,
  };

  const setPeriod = (next: PeriodFilter) =>
    void navigate({ search: (prev: Search) => ({ ...prev, de: next.from, ate: next.to }) });

  if (profile && profile.role !== "ADMIN") {
    return (
      <>
        <AdminTopbar title="Relatórios" />
        <AdminCard className="m-8 p-8 text-center text-sm text-mb-muted">
          Relatórios têm custo e lucro da empresa — só administradores acessam.
        </AdminCard>
      </>
    );
  }

  return (
    <>
      <AdminTopbar title="Relatórios" />

      <div className="flex flex-col gap-6 p-4 sm:p-8">
        {/* Revendedores tem filtro próprio (campanha, UTM, peça...) e CSV próprio. */}
        {aba !== "revendedores" && <Filters period={period} onChange={setPeriod} />}

        <div className="flex items-center gap-2 border-b border-mb-line">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() =>
                // Troca de aba leva só o período: filtro de campanha é da aba dela.
                void navigate({
                  search: (prev: Search) => ({ aba: tab.value, de: prev.de, ate: prev.ate }),
                })
              }
              className={cn(
                "-mb-px border-b-2 px-3 pb-3 text-sm font-bold transition-colors",
                aba === tab.value
                  ? "border-mb-navy text-mb-navy"
                  : "border-transparent text-mb-muted hover:text-mb-navy",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {aba === "geral" && <Overview period={period} />}
        {aba === "pedidos" && <Orders period={period} />}
        {aba === "retencao" && <Retention period={period} />}
        {aba === "revendedores" && (
          <ResellersTab
            filters={searchToQuery(search, period)}
            onChange={(next) =>
              void navigate({ search: { aba: "revendedores", ...queryToSearch(next) } })
            }
            onOpenCampaign={(campanhaId) =>
              void navigate({
                to: "/admin/relatorios/campanhas/$campanhaId",
                params: { campanhaId },
                search: { de: period.from, ate: period.to },
              })
            }
          />
        )}
      </div>
    </>
  );
}

/**
 * Período e exportação, numa linha só acima de tudo.
 *
 * Presets primeiro (é o que se usa quase sempre); datas livres ao lado para o
 * resto. Os CSVs saem do servidor com o mesmo período.
 */
function Filters({
  period,
  onChange,
}: {
  period: PeriodFilter;
  onChange: (next: PeriodFilter) => void;
}) {
  const [exporting, setExporting] = useState<string | null>(null);
  const exportCsv = async (kind: (typeof EXPORTS)[number]["kind"]) => {
    setExporting(kind);
    try {
      await downloadReport(kind, period);
    } catch (cause) {
      toast.error(errorMessage(cause));
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <PeriodPicker period={period} onChange={onChange} />

      <div className="ml-auto flex items-center gap-1.5">
        <span className="text-xs text-mb-muted">Exportar CSV:</span>
        {EXPORTS.map((item) => (
          <button
            key={item.kind}
            type="button"
            disabled={exporting !== null}
            onClick={() => void exportCsv(item.kind)}
            title={`${item.label} — ${periodLabel(period.from, period.to)}`}
            className="flex h-9 items-center gap-1.5 rounded-full border border-mb-line bg-white px-3 text-xs font-bold text-mb-navy transition-colors hover:border-mb-navy disabled:opacity-50"
          >
            {exporting === item.kind ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Enquanto um período novo carrega, o anterior fica na tela em opacidade
 * reduzida — sem esqueleto piscando nem layout pulando.
 */
function Frame({
  loading,
  error,
  hasData,
  children,
}: {
  loading: boolean;
  error: unknown;
  hasData: boolean;
  children: React.ReactNode;
}) {
  if (!hasData) {
    return (
      <AdminCard>
        <ListState loading={loading} error={error} empty={!loading} emptyLabel="Sem dados." />
      </AdminCard>
    );
  }
  return <div className={cn("transition-opacity", loading && "opacity-60")}>{children}</div>;
}

function Overview({ period }: { period: PeriodFilter }) {
  const report = useOverviewReport(period);
  return (
    <Frame loading={report.isFetching} error={report.error} hasData={Boolean(report.data)}>
      {report.data && <OverviewTab report={report.data} />}
    </Frame>
  );
}

function Orders({ period }: { period: PeriodFilter }) {
  const report = useOrdersReport(period);
  return (
    <Frame loading={report.isFetching} error={report.error} hasData={Boolean(report.data)}>
      {report.data && <OrdersTab report={report.data} />}
    </Frame>
  );
}

function Retention({ period }: { period: PeriodFilter }) {
  const report = useRetentionReport(period);
  return (
    <Frame loading={report.isFetching} error={report.error} hasData={Boolean(report.data)}>
      {report.data && <RetentionTab report={report.data} />}
    </Frame>
  );
}
